import toast from "react-hot-toast";
import {
  User,
  Plus,
  Edit2,
  Trash2,
  Users,
  Mail,
  Phone,
  PhoneCall,
  Search,
  Video,
} from "lucide-react";
import { useState } from "react";
import ConfirmModal from "../modal/ConfirmModal";
import ContactModal from "../modal/ContactModal";
import { useContacts } from "../hooks/useContacts";
import { useWhatsAppCall } from "../context/WhatsAppCallContext";

const Contacts = () => {
  const { contacts, addContact, updateContact, deleteContact, loading, error } =
    useContacts();
  const { available, canMakeCalls, busy, startCall, videoSupported } = useWhatsAppCall();
  const showCall = available && canMakeCalls;

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const filteredContacts = contacts.filter((contact) => {
    const term = searchTerm.toLowerCase();
    const nameMatch = contact.name?.toLowerCase().includes(term);
    const emailMatch = contact.email?.toLowerCase().includes(term);
    const phoneNumberMatch = contact.phoneNumber?.toLowerCase().includes(term);
    return nameMatch || emailMatch || phoneNumberMatch;
  });

  const openContactModal = () => {
    setSelectedContact(null);
    setIsModalOpen(true);
  };

  const handleSaveContact = async (contactData) => {
    try {
      if (selectedContact) {
        await updateContact(selectedContact.id, contactData);
        toast.success("Contato atualizado com sucesso!");
      } else {
        await addContact(contactData);
        toast.success("Contato adicionado com sucesso!");
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao salvar o contato.");
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const handleDeleteContact = (contactId) => {
    setContactToDelete(contactId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (contactToDelete) {
        await deleteContact(contactToDelete);
        toast.success("Contato excluído com sucesso!");
        setContactToDelete(null);
        setConfirmOpen(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir o contato.");
    }
  };

  const getOriginCount = (origin) => {
    return contacts.filter((c) => c.origin === origin).length;
  };

  if (loading) return <p>Carregando contatos...</p>;
  if (error) return <p className="text-red-500">Erro: {error}</p>;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestão de Contatos
          </h1>
          <button
            onClick={openContactModal}
            className="rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
          >
            <Plus className="mr-2 inline h-4 w-4" />
            Novo Contato
          </button>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <Users className="mx-auto mb-2 h-8 w-8 text-blue-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {contacts.length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total de Contatos
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <Mail className="mx-auto mb-2 h-8 w-8 text-green-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {contacts.filter((c) => c.email).length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Com Email
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <Phone className="mx-auto mb-2 h-8 w-8 text-purple-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {contacts.filter((c) => c.phoneNumber).length}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Com Telefone
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
            <User className="mx-auto mb-2 h-8 w-8 text-yellow-500" />
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {getOriginCount("MANUAL")}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Manuais</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-colors duration-300 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Lista de Contatos
            </h2>

            <div className="relative w-64">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Telefone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Cargo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Origem
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    title="Situação na agenda do Google"
                  >
                    Google
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {filteredContacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <User className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {contact.name?.endsWith("@c.us")
                            ? contact.name.replace(/\D/g, "")
                            : contact.name}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <Mail className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {contact.email || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center">
                        <Phone className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {contact.phoneNumber.replace(/\D/g, "")}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      {contact.work || "-"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                          contact.origin === "MANUAL"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : contact.origin === "WEBSITE"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : contact.origin === "GOOGLE_ADS"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                                : contact.origin === "INDICATION"
                                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                  : "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200"
                        }`}
                      >
                        {contact.origin}
                      </span>
                    </td>

                    <td className="whitespace-nowrap px-6 py-4">
                      {contact.googleSyncStatus === "SYNCED" ? (
                        <span
                          className="inline-block rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          title={
                            contact.googleSyncedAt
                              ? `Salvo em ${new Date(contact.googleSyncedAt).toLocaleString("pt-BR")}`
                              : "Salvo na agenda"
                          }
                        >
                          Salvo
                        </span>
                      ) : contact.googleSyncStatus === "FAILED" ? (
                        <span
                          className="inline-block rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300"
                          title={contact.googleSyncError || "Falha ao salvar"}
                        >
                          Falhou
                        </span>
                      ) : contact.googleSyncStatus === "SKIPPED" ? (
                        <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                          Ignorado
                        </span>
                      ) : (
                        <span className="inline-block rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          Pendente
                        </span>
                      )}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {/* A ligação sai pela conversa do contato (histórico e
                            permissão de acesso); contato sem conversa não liga. */}
                        {showCall && (
                          <>
                            <button
                              onClick={() => startCall(contact.conversationId)}
                              disabled={busy || !contact.conversationId}
                              className="rounded p-1 text-green-600 transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-green-400 dark:hover:bg-green-900/20"
                              title={
                                !contact.conversationId
                                  ? "Contato sem conversa: envie uma mensagem antes de ligar"
                                  : busy
                                    ? "Encerre a ligação atual para ligar"
                                    : "Ligar pelo WhatsApp"
                              }
                            >
                              <PhoneCall className="h-4 w-4" />
                            </button>
                            {videoSupported && (
                              <button
                                onClick={() => startCall(contact.conversationId, { video: true })}
                                disabled={busy || !contact.conversationId}
                                className="rounded p-1 text-green-600 transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-green-400 dark:hover:bg-green-900/20"
                                title={
                                  !contact.conversationId
                                    ? "Contato sem conversa: envie uma mensagem antes de ligar"
                                    : "Videochamada pelo WhatsApp"
                                }
                              >
                                <Video className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveContact}
        contact={selectedContact}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Excluir Contato"
        message="Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default Contacts;
